// "Brought by Hayven": every visit is credited to ONE source, the first that matches in this order (precedence),
// so the Impact page, the Clients page and the Marketing page show the same numbers and never count a visit twice.
// The five Hayven sources are the total; the two fallbacks only appear where every new client needs a source
// (Clients page, new clients by source). Text blasts, in-salon offer claims, prizes and plain Bio Page bookings
// reach people already coming, so they are never in the total. The SQL lives in
// `apps/api/src/services/reports/attribution/visit-source-sql.ts`.
// ---------------------------------------------------------------------------
import type { OfferPlacement } from '../marketing/new-client-offer/new-client-offer-enums';

/** Hayven sources, in precedence order (first match wins). */
export const HAYVEN_VISIT_SOURCES = ['MISSED_CALL', 'REFERRAL', 'CREATOR', 'CAMPAIGN', 'OFFER_ONLINE'] as const;
export type HayvenVisitSource = (typeof HAYVEN_VISIT_SOURCES)[number];

/** Sources of a visit Hayven did not bring: booked on the online booking page, or anything else. */
export const FALLBACK_VISIT_SOURCES = ['BOOKED_ONLINE', 'OTHER'] as const;
export type FallbackVisitSource = (typeof FALLBACK_VISIT_SOURCES)[number];

export type VisitSource = HayvenVisitSource | FallbackVisitSource;

/** Every source, in precedence order: the SQL ranks a visit by its position here, so this is the one place the order lives. */
export const VISIT_SOURCE_PRECEDENCE = [...HAYVEN_VISIT_SOURCES, ...FALLBACK_VISIT_SOURCES] as const satisfies readonly VisitSource[];

export const isHayvenVisitSource = (source: VisitSource): source is HayvenVisitSource =>
  (HAYVEN_VISIT_SOURCES as readonly VisitSource[]).includes(source);

export const VISIT_SOURCE_LABELS: Record<VisitSource, string> = {
  MISSED_CALL: 'Missed calls saved',
  REFERRAL: 'Friends of your clients',
  CREATOR: 'Creators',
  CAMPAIGN: 'Ads & offer pages',
  OFFER_ONLINE: 'New Client Offer',
  BOOKED_ONLINE: 'Booked online',
  OTHER: 'Walk-in / other',
};

/** One short line under each Hayven source on the Impact table: how the visit got its credit. */
export const HAYVEN_VISIT_SOURCE_HOW: Record<HayvenVisitSource, string> = {
  MISSED_CALL: 'Callers you missed who booked from Hayven’s text, or with the AI on the call',
  REFERRAL: 'Friends your clients invited, on their first visit',
  CREATOR: 'Followers of creators you work with',
  CAMPAIGN: 'Signed up on your ad’s page, then came in',
  OFFER_ONLINE: 'Claimed your offer online, then came in',
};

/**
 * How each New Client Offer sign-up place counts. ONLINE = "claimed online" (`OFFER_ONLINE`): the Bio Page, the booking
 * page banner, the embed on the salon's website, a campaign landing page (a landing-page claim whose visit carries
 * a campaign credit is a `CAMPAIGN` visit, by precedence) and a giveaway thank-you pass (texted to an entrant who signed
 * up on the giveaway page). OWN_SOURCE = the sign-up belongs to its own source (Refer a Friend, Creators).
 * IN_SALON = joined at the salon (staff, kiosk, counter QR): never Hayven-brought. PHONE_CALL is retired and sits with
 * them so it never counts as online. A `Record`, so a new placement fails to compile until someone decides how it counts.
 */
export const OFFER_PLACEMENT_VISIT_CREDIT: Record<OfferPlacement, 'ONLINE' | 'OWN_SOURCE' | 'IN_SALON'> = {
  CAMPAIGN_LANDING: 'ONLINE',
  BIO_PAGE_SHEET: 'ONLINE',
  BIO_PAGE_BANNER: 'ONLINE',
  BOOKING_BANNER: 'ONLINE',
  WEBSITE_EMBED: 'ONLINE',
  GIVEAWAY: 'ONLINE',
  REFERRAL: 'OWN_SOURCE',
  CREATOR: 'OWN_SOURCE',
  STAFF: 'IN_SALON',
  KIOSK: 'IN_SALON',
  COUNTER_QR: 'IN_SALON',
  PHONE_CALL: 'IN_SALON',
};

/** The placements that make a used offer an `OFFER_ONLINE` visit. */
export const ONLINE_OFFER_PLACEMENTS = (Object.keys(OFFER_PLACEMENT_VISIT_CREDIT) as OfferPlacement[])
  .filter((placement) => OFFER_PLACEMENT_VISIT_CREDIT[placement] === 'ONLINE');
